/**
 * A aba "Banco de Horas Equipe FBV": o resumo e a tela de cada colaborador.
 *
 * Papel: lê o banco de horas por `useBancoDeHoras` e desenha o
 * `ResumoBancoHoras` ou o `DetalheBancoHoras`, conforme `tela`, com as caixas,
 * as confirmações, a importação e as impressões que cada botão abre.
 *
 * Como usar: quem monta guarda `tela` e a recebe de volta por `onTrocarTela` —
 * a aba não guarda em que tela está, para que trocar de aba e voltar encontre
 * a mesma pessoa aberta, como na origem. Sem `podeEditar`, nenhum botão de
 * gravação aparece. As folhas de impressão são montadas por quem monta a aba,
 * fora do bloco da tela, para o resto da página sair da impressão.
 *
 * Se o colaborador de `tela` não está mais na lista — removido aqui ou por
 * outra pessoa —, a aba mostra o resumo, como `bhRenderEmployee` faz.
 *
 * A primeira leitura mostra o esqueleto, e a falha dela, o bloco "Não foi
 * possível carregar" com "Tentar de novo". Uma releitura que falha com os dados
 * já na tela mantém os dados: a TanStack Query guarda o `data` anterior, e
 * trocá-lo pela falha esconderia o que a pessoa estava conferindo.
 *
 * As remoções pedem o `confirm()` com o texto da origem, e a falha delas sai
 * num `alert()`. A caixa aberta leva o colaborador do momento do clique.
 */
import { type ColaboradorBancoHoras, type LancamentoMensal } from '@concierge/shared';
import { useState } from 'react';

import { ErroDaApi } from '@/shared/api';
import { formatarCompetencia } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  useBancoDeHoras,
  useExcluirColaboradorBancoHoras,
  useExcluirLancamentoMensal,
} from '../hooks/usar-banco-horas';
import { DetalheBancoHoras } from './detalhe-banco-horas';
import { FormularioColaboradorBancoHoras } from './formulario-colaborador-banco-horas';
import { FormularioMes } from './formulario-mes';
import { ImportacaoSecullum } from './importacao-secullum';
import { ResumoBancoHoras } from './resumo-banco-horas';

/** Em que tela a aba está: o resumo ou a de um colaborador, pelo `id`. */
export type TelaDoBanco = { modo: 'resumo' } | { modo: 'colaborador'; id: string };

/** A caixa aberta na aba, com o que ela precisa no momento do clique. */
type CaixaAberta =
  | { tipo: 'mes'; colaborador: ColaboradorBancoHoras; mes: LancamentoMensal | null }
  | { tipo: 'colaborador'; colaborador: ColaboradorBancoHoras | null }
  | { tipo: 'importacao' };

/** Quantas linhas de espera o esqueleto mostra. */
const LINHAS_DE_ESPERA = 3;

/** O que o `alert()` diz numa remoção que falhou sem resposta da API. */
const FALHA_AO_REMOVER = 'A exclusão falhou antes de chegar ao servidor.';

interface AbaBancoHorasProps {
  tela: TelaDoBanco;
  onTrocarTela: (tela: TelaDoBanco) => void;
  /** Se quem abre tem nível `editar` em `rh`. */
  podeEditar: boolean;
  /** Monta a folha do resumo com a lista do momento do clique. */
  onImprimirResumo: (colaboradores: readonly ColaboradorBancoHoras[]) => void;
  /** Monta a folha do detalhamento do colaborador aberto. */
  onImprimirColaborador: (colaborador: ColaboradorBancoHoras) => void;
}

/**
 * A aba do banco de horas.
 *
 * @param tela - A tela guardada por quem monta.
 * @param onTrocarTela - Chamado com a tela nova ao abrir um colaborador ou voltar.
 * @param podeEditar - Sem ele, só o que é leitura aparece.
 * @param onImprimirResumo - O "🖨 Imprimir / PDF" do resumo; leitura, e por isso em qualquer nível.
 * @param onImprimirColaborador - O "🖨 Imprimir / PDF" do colaborador.
 * @returns O esqueleto, a falha, ou o resumo ou a tela do colaborador com a caixa aberta.
 */
export function AbaBancoHoras({
  tela,
  onTrocarTela,
  podeEditar,
  onImprimirResumo,
  onImprimirColaborador,
}: AbaBancoHorasProps) {
  const consulta = useBancoDeHoras();
  const excluirLancamento = useExcluirLancamentoMensal();
  const excluirColaborador = useExcluirColaboradorBancoHoras();
  const [caixa, setCaixa] = useState<CaixaAberta | null>(null);

  /** Remove o mês, depois do `confirm()` de `bhDeleteEntry`. */
  async function handleRemoverMes(mes: LancamentoMensal) {
    if (!window.confirm(`Excluir o registro de ${formatarCompetencia(mes.competencia)}?`))
      return;
    try {
      await excluirLancamento.mutateAsync(mes.id);
    } catch (erro) {
      window.alert(erro instanceof ErroDaApi ? erro.message : FALHA_AO_REMOVER);
    }
  }

  /** Remove o colaborador e o histórico, depois do `confirm()` de `bhDeleteEmployee`. */
  async function handleRemoverColaborador(colaborador: ColaboradorBancoHoras) {
    if (
      !window.confirm(
        'Remover este colaborador do Banco de Horas? Todo o histórico de meses será perdido.',
      )
    ) {
      return;
    }
    try {
      await excluirColaborador.mutateAsync(colaborador.id);
      onTrocarTela({ modo: 'resumo' });
    } catch (erro) {
      window.alert(erro instanceof ErroDaApi ? erro.message : FALHA_AO_REMOVER);
    }
  }

  if (consulta.data === undefined) {
    if (consulta.isError) {
      return (
        <div className="rounded-md border border-dashed border-border bg-card px-6 py-12 text-center">
          <p className="font-serif text-lg text-foreground">Não foi possível carregar</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
            {consulta.error instanceof ErroDaApi
              ? consulta.error.message
              : 'A consulta falhou antes de chegar ao servidor.'}
          </p>
          <Button variant="outline" className="mt-4" onClick={() => void consulta.refetch()}>
            Tentar de novo
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: LINHAS_DE_ESPERA }, (_, indice) => (
          <Skeleton key={indice} className="h-12 rounded-md" />
        ))}
      </div>
    );
  }

  const banco = consulta.data;
  const aberto =
    tela.modo === 'colaborador'
      ? banco.colaboradores.find((colaborador) => colaborador.id === tela.id)
      : undefined;

  return (
    <>
      {aberto === undefined ? (
        <ResumoBancoHoras
          atualizacao={banco.atualizacao}
          colaboradores={banco.colaboradores}
          onAbrir={(id) => onTrocarTela({ modo: 'colaborador', id })}
          onImprimir={() => onImprimirResumo(banco.colaboradores)}
          onImportar={podeEditar ? () => setCaixa({ tipo: 'importacao' }) : undefined}
          onNovo={
            podeEditar ? () => setCaixa({ tipo: 'colaborador', colaborador: null }) : undefined
          }
        />
      ) : (
        <DetalheBancoHoras
          colaborador={aberto}
          onVoltar={() => onTrocarTela({ modo: 'resumo' })}
          onImprimir={() => onImprimirColaborador(aberto)}
          onAdicionarMes={
            podeEditar
              ? () => setCaixa({ tipo: 'mes', colaborador: aberto, mes: null })
              : undefined
          }
          onEditarMes={
            podeEditar
              ? (mes) => setCaixa({ tipo: 'mes', colaborador: aberto, mes })
              : undefined
          }
          onRemoverMes={podeEditar ? (mes) => void handleRemoverMes(mes) : undefined}
          onEditarColaborador={
            podeEditar
              ? () => setCaixa({ tipo: 'colaborador', colaborador: aberto })
              : undefined
          }
          onRemoverColaborador={
            podeEditar ? () => void handleRemoverColaborador(aberto) : undefined
          }
        />
      )}

      {caixa?.tipo === 'mes' && (
        <FormularioMes
          key={caixa.mes?.id ?? 'novo'}
          colaborador={caixa.colaborador}
          mes={caixa.mes}
          onSalvo={() => setCaixa(null)}
          onCancelar={() => setCaixa(null)}
        />
      )}

      {caixa?.tipo === 'colaborador' && (
        <FormularioColaboradorBancoHoras
          key={caixa.colaborador?.id ?? 'novo'}
          colaborador={caixa.colaborador}
          onSalvo={() => setCaixa(null)}
          onCancelar={() => setCaixa(null)}
        />
      )}

      {caixa?.tipo === 'importacao' && (
        <ImportacaoSecullum
          colaboradores={banco.colaboradores}
          onFechar={() => setCaixa(null)}
        />
      )}
    </>
  );
}
